import { TypedDocumentNode, gql } from '@apollo/client';
import { useParams } from 'react-router-dom';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { ANY_SPORT_MY_CLUB } from '@sorare/core/src/constants/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useDeeplinkFromNativeApp from '@sorare/core/src/hooks/useDeeplinkFromNativeApp';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import useUpdateSportProfile from '@sorare/core/src/hooks/useUpdateSportProfile';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { ClubEditor } from 'components/profile/Header/ClubEditor';

import {
  ClubEditorPageQuery,
  ClubEditorPageQueryVariables,
} from './__generated__/index.graphql';

const CLUB_EDITOR_PAGE_QUERY = gql`
  query ClubEditorPageQuery($slug: String!, $sport: Sport!) {
    user(slug: $slug) {
      slug
      userSportProfile(sport: $sport) {
        ...ClubEditor_userSportProfile
      }
    }
  }
  ${ClubEditor.fragments.userSportProfile}
` as TypedDocumentNode<ClubEditorPageQuery, ClubEditorPageQueryVariables>;

const ClubEditorPage = ({ sport }: { sport: Sport }) => {
  const { slug = '' } = useParams();
  const { currentUser } = useCurrentUserContext();
  const {
    flags: { useEditableStatisticsInManagerId = false },
  } = useFeatureFlags();
  const { navigateWithDeeplink } = useDeeplinkFromNativeApp();
  const updateSportProfile = useUpdateSportProfile();

  const isMe = slug === currentUser?.slug;

  const { data, loading } = useQuery(CLUB_EDITOR_PAGE_QUERY, {
    variables: { slug, sport },
    skip: !isMe || !slug,
  });

  if (!isMe) return null;
  if (loading) return <LoadingIndicator fullHeight />;
  if (!data?.user.userSportProfile) return null;

  return (
    <ClubEditor
      open
      onClose={() =>
        navigateWithDeeplink(
          generateSportPath(ANY_SPORT_MY_CLUB, {
            sport,
            params: { slug },
          })
        )
      }
      onSubmit={async ({
        clubName,
        selectedStatistics,
        backgroundId,
        clubBadgeId,
        achievementBadgesIds,
      }) =>
        updateSportProfile({
          sport,
          clubName,
          ...(useEditableStatisticsInManagerId ? { selectedStatistics } : {}),
          backgroundId,
          clubBadgeId,
          achievementBadgesIds,
        })
      }
      userSportProfile={data.user.userSportProfile}
    />
  );
};

export default ClubEditorPage;
